import {
  changeReadStatus,
  getNotifications,
  getUnreadCount,
} from '@/services/api/notifications';
import type { ApiErrorResponse } from '@/types/api/common';
import type {
  NotificationData,
  NotificationsListingRequest,
  NotificationsListingResponse,
  UnreadCountResponse,
} from '@/types/api/notification';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

export const useNotifications = ({
  limit = 10,
  page = 1,
  type,
  orderBy = 'createdDate:desc',
  isGetNotificationsDisabled = false,
}: Partial<
  NotificationsListingRequest & {
    isGetNotificationsDisabled?: boolean;
  }
>) => {
  // Regular query for notifications
  const getNotificationsApi = useQuery<
    NotificationsListingResponse,
    ApiErrorResponse
  >({
    queryKey: ['notifications', page, limit, type, orderBy],
    queryFn: () =>
      getNotifications({
        page,
        limit,
        type,
        orderBy,
      } as NotificationsListingRequest),
    refetchOnWindowFocus: false,
    enabled: !isGetNotificationsDisabled,
  });

  // Infinite query for notifications
  const getInfiniteNotificationsApi = useInfiniteQuery({
    queryKey: [
      'infinite-notifications',
      limit.toString(),
      type || 'null',
      orderBy,
    ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      getNotifications({
        page: pageParam,
        limit,
        type,
        orderBy,
      } as NotificationsListingRequest),
    getNextPageParam: (lastPage, _, lastPageParam) => {
      // Calculate if there are more pages
      const totalPages = Math.ceil(lastPage.totalCount / limit);
      if (lastPageParam < totalPages) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    refetchOnWindowFocus: false,
    enabled: !isGetNotificationsDisabled,
  });

  // Unread count query
  const getUnreadCountApi = useQuery<UnreadCountResponse, ApiErrorResponse>({
    queryKey: ['notifications-unread-count'],
    queryFn: getUnreadCount,
  });

  const getUnreadCountData = () => {
    return getUnreadCountApi.data?.data;
  };

  // Helper function to get all notifications from infinite query
  const getAllNotifications = (): NotificationData[] => {
    if (!getInfiniteNotificationsApi.data) return [];

    // Flatten the pages and extract all notifications
    return getInfiniteNotificationsApi.data.pages.reduce<NotificationData[]>(
      (acc, page) => {
        return [...acc, ...page.data];
      },
      []
    );
  };

  const changeNotificationReadStatus = async (
    notificationId: string,
    read: boolean
  ) => {
    try {
      await changeReadStatus({
        notificationId,
        read,
      });
      // mutate the notification in the list
      getInfiniteNotificationsApi.refetch();
      getNotificationsApi.refetch();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  return {
    getNotificationsApi,
    getInfiniteNotificationsApi,
    getUnreadCountApi,
    getAllNotifications,
    getUnreadCountData,
    changeNotificationReadStatus,
  };
};
